import React, { createContext, useState, useEffect, useContext } from 'react';
import { useAuth } from './AuthContext';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import { logger } from '../utils/logger';
import supabase from '../config/supabase';

export const CompanyContext = createContext();

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (!context) {
    throw new Error('useCompany deve ser usado dentro de um CompanyProvider');
  }
  return context;
};

export const CompanyProvider = ({ children }) => {
  const [selectedCompany, setSelectedCompany] = useState(() => {
    const storedCompany = localStorage.getItem('selectedCompany');
    return storedCompany ? JSON.parse(storedCompany) : null;
  });
  const { user } = useAuth();

  // Função para carregar empresas do usuário
  const fetchUserCompanies = async () => {
    if (!user) return null;

    try {
      logger.log('[CompanyContext] Carregando empresas do usuário...');
      const authInstance = getAuth();
      const idToken = await authInstance.currentUser.getIdToken();
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://iat-backend-staging-145011075010.us-central1.run.app';

      const response = await axios.get(`${API_BASE}/api/user-companies`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
      });

      if (response.data && response.data.companies) {
        const userCompanies = response.data.companies;
        logger.log('[CompanyContext] Empresas carregadas:', userCompanies);

        if (userCompanies.length === 1) {
          // Se há apenas uma empresa, selecionar automaticamente
          const company = userCompanies[0];
          logger.log('[CompanyContext] Selecionando única empresa:', company);
          setSelectedCompany(company);
          return company;
        } else if (userCompanies.length > 1) {
          // Se há múltiplas empresas, usar a primeira (ou a última selecionada)
          const storedCompany = localStorage.getItem('selectedCompany');
          if (storedCompany) {
            const parsed = JSON.parse(storedCompany);
            const foundCompany = userCompanies.find(c => c.companyId === parsed.companyId);
            if (foundCompany) {
              logger.log('[CompanyContext] Usando empresa armazenada:', foundCompany);
              setSelectedCompany(foundCompany);
              return foundCompany;
            }
          }
          // Se não encontrou a empresa armazenada, usar a primeira
          const firstCompany = userCompanies[0];
          logger.log('[CompanyContext] Selecionando primeira empresa:', firstCompany);
          setSelectedCompany(firstCompany);
          return firstCompany;
        }
      }

      return null;
    } catch (error) {
      console.error('[CompanyContext] Erro ao carregar empresas:', error);
      return null;
    }
  };

  // Carregar automaticamente empresas quando usuário estiver autenticado
  useEffect(() => {
    logger.log('[CompanyContext] useEffect executado - user:', !!user, 'selectedCompany:', !!selectedCompany?.companyId);
    
    // O RegisterScreen grava uma empresa parcial ({companyId, role, companyName})
    // para navegar logo após o cadastro — sem os dados de assinatura o card da
    // conta mostra "não definido". Detecta e completa via /api/user-companies.
    const isPartialCompany = !!selectedCompany?.companyId && selectedCompany?.subscriptionStatus === undefined;

    const loadCompaniesIfNeeded = async () => {
      if (user && (!selectedCompany?.companyId || isPartialCompany)) {
        logger.log('[CompanyContext] Usuário autenticado mas sem empresa - carregando...');
        const result = await fetchUserCompanies();
        logger.log('[CompanyContext] Resultado do carregamento:', result);
      } else if (!user && !selectedCompany?.companyId) {
        // Tentar carregar empresas mesmo sem user no contexto (fallback)
        logger.log('[CompanyContext] Tentando carregar empresas sem user no contexto...');
        try {
          const authInstance = getAuth();
          const currentUser = authInstance.currentUser;
          if (currentUser) {
            logger.log('[CompanyContext] Usuário encontrado no getAuth() - carregando empresas...');
            const result = await fetchUserCompanies();
            logger.log('[CompanyContext] Resultado do carregamento (fallback):', result);
          } else {
            logger.log('[CompanyContext] Nenhum usuário encontrado no getAuth()');
          }
        } catch (error) {
          console.error('[CompanyContext] Erro no fallback de carregamento:', error);
        }
      } else {
        logger.log('[CompanyContext] Condições não atendidas para carregar empresas');
        logger.log('[CompanyContext] user:', !!user);
        logger.log('[CompanyContext] selectedCompany?.companyId:', !!selectedCompany?.companyId);
      }
    };

    loadCompaniesIfNeeded();
  }, [user, selectedCompany?.companyId]);

  // Listener em tempo real para mudanças na empresa (Supabase Realtime)
  useEffect(() => {
    if (!user || !selectedCompany?.companyId) return;

    // Se Supabase não estiver configurado, ignorar silenciosamente
    if (!supabase) {
      logger.warn('[CompanyContext] Supabase não configurado — listener real-time desativado');
      return;
    }

    const channel = supabase
      .channel(`company-${selectedCompany.companyId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'companies',
          filter: `firestore_id=eq.${selectedCompany.companyId}`,
        },
        (payload) => {
          const row = payload.new;
          const updatedCompany = {
            ...selectedCompany,
            name: row.name,
            subscriptionPlan: row.subscription_plan,
            settings: row.settings,
            companyId: selectedCompany.companyId,
          };
          setSelectedCompany(updatedCompany);
          logger.log('[CompanyContext] Empresa atualizada em tempo real (Supabase):', updatedCompany);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          logger.log('[CompanyContext] Supabase Realtime conectado para empresa:', selectedCompany.companyId);
        }
        if (status === 'CHANNEL_ERROR') {
          logger.warn('[CompanyContext] Erro no canal Supabase Realtime');
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedCompany?.companyId, user]);

  useEffect(() => {
    if (selectedCompany) {
      try {
        localStorage.setItem('selectedCompany', JSON.stringify(selectedCompany));
        logger.log('Empresa atualizada no localStorage:', selectedCompany);
      } catch (error) {
        console.error('Erro ao salvar empresa no localStorage:', error);
      }
    } else {
      localStorage.removeItem('selectedCompany');
      logger.log('Empresa removida do localStorage');
    }
  }, [selectedCompany]);

  const updateSelectedCompany = (company) => {
    try {
      setSelectedCompany(company);
      logger.log('Empresa atualizada no contexto:', company);
    } catch (error) {
      console.error('Erro ao atualizar empresa no contexto:', error);
    }
  };

  return (
    <CompanyContext.Provider value={{ selectedCompany, updateSelectedCompany }}>
      {children}
    </CompanyContext.Provider>
  );
};
